export const CloverVoidOutcome = {
    VOIDED: 'voided',
    REFUNDED: 'refunded',
    REJECTED: 'rejected',
    MALFORMED_RESPONSE: 'malformed_response',
} as const;

export type CloverVoidOutcome = (typeof CloverVoidOutcome)[keyof typeof CloverVoidOutcome];

export const CloverVoidReason = {
    USER_CANCEL: 'USER_CANCEL',
    TRANSPORT_ERROR: 'TRANSPORT_ERROR',
    REJECT_SIGNATURE: 'REJECT_SIGNATURE',
    REJECT_PARTIAL_AUTH: 'REJECT_PARTIAL_AUTH',
    NOT_APPROVED: 'NOT_APPROVED',
    FAILED: 'FAILED',
    AUTH_CLOSED_NEW_CARD: 'AUTH_CLOSED_NEW_CARD',
    DEVELOPER_PAY_PARTIAL_AUTH: 'DEVELOPER_PAY_PARTIAL_AUTH',
    REJECT_DUPLICATE: 'REJECT_DUPLICATE',
    REJECT_OFFLINE: 'REJECT_OFFLINE',
    GIFTCARD_LOAD_FAILED: 'GIFTCARD_LOAD_FAILED',
    USER_GIFTCARD_LOAD_CANCEL: 'USER_GIFTCARD_LOAD_CANCEL',
    DEVELOPER_PAY_TIP_ADJUST_FAILED: 'DEVELOPER_PAY_TIP_ADJUST_FAILED',
    USER_CUSTOMER_CANCEL: 'USER_CUSTOMER_CANCEL',
    FRAUD: 'FRAUD',
} as const;

export type CloverVoidReason = (typeof CloverVoidReason)[keyof typeof CloverVoidReason];

export const CloverVoidStatus = {
    SENT_TO_SERVER: 'SENT_TO_SERVER',
    IN_DEVICE_QUEUE: 'IN_DEVICE_QUEUE',
} as const;

export type CloverVoidStatus = (typeof CloverVoidStatus)[keyof typeof CloverVoidStatus];

export const CloverVoidPaymentResult = {
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL',
    INITIATED: 'INITIATED',
    VOIDED: 'VOIDED',
    VOIDING: 'VOIDING',
    VOID_FAILED: 'VOID_FAILED',
    AUTH: 'AUTH',
    AUTH_COMPLETED: 'AUTH_COMPLETED',
    DISCOUNT: 'DISCOUNT',
} as const;

export type CloverVoidPaymentResult =
    (typeof CloverVoidPaymentResult)[keyof typeof CloverVoidPaymentResult];

export interface CloverCloudPayVoidRequestHeaders {
    readonly deviceId: string;
    readonly idempotencyKey: string;
    readonly posId: string;
    readonly userAgent: string;
    readonly timeoutSeconds?: number;
}

export interface CloverCloudPayVoidRequestBody {
    readonly voidReason: CloverVoidReason;
}

export interface CloverCloudPayVoidRequestDetails {
    readonly method: 'POST';
    readonly path: '/v1/payments/{paymentId}/void';
    readonly paymentId: string;
    readonly headers: CloverCloudPayVoidRequestHeaders;
    readonly body: CloverCloudPayVoidRequestBody;
}

export interface CloverEcommerceVoidRequestDetails {
    readonly method: 'POST';
    readonly path: '/v1/refunds';
    readonly chargeId: string;
    readonly headers: {
        readonly userAgent: string;
    };
    readonly body: {
        readonly charge: string;
    };
}

export type CloverVoidRequestDetails =
    | CloverCloudPayVoidRequestDetails
    | CloverEcommerceVoidRequestDetails;

export interface CloverVoidReferenceBody {
    readonly id?: string;
}

export interface CloverVoidCardTransactionBody {
    readonly authCode?: string;
    readonly referenceId?: string;
    readonly state?: string;
    readonly token?: string;
    readonly transactionNo?: string;
    readonly type?: string;
}

export interface CloverVoidPaymentBody {
    readonly id?: string;
    readonly amount?: number;
    readonly result?: CloverVoidPaymentResult;
    readonly cardTransaction?: CloverVoidCardTransactionBody;
    readonly externalPaymentId?: string;
    readonly externalReferenceId?: string;
    readonly refunds?: readonly unknown[];
    readonly voidPaymentRef?: CloverVoidReferenceBody;
    readonly voidReason?: CloverVoidReason;
}

export interface CloverCloudPayVoidResponseBody {
    readonly payment?: CloverVoidPaymentBody;
    readonly paymentId: string;
    readonly voidReason?: CloverVoidReason;
    readonly voidStatus?: CloverVoidStatus;
}

export interface CloverEcommerceVoidResponseBody {
    readonly id: string;
    readonly object: 'refund';
    readonly amount: number;
    readonly charge: string;
    readonly created?: number;
    readonly status: 'succeeded';
    readonly metadata?: {
        readonly refundType?: string;
        readonly authCode?: string;
        readonly refNum?: string;
        readonly gatewayResponseCode?: string;
    };
}

export type CloverVoidResponseBody =
    | CloverCloudPayVoidResponseBody
    | CloverEcommerceVoidResponseBody;

export interface CloverVoidErrorBody {
    readonly message: string;
    readonly type: string;
    readonly code?: string;
    readonly requestId?: string;
    readonly requestType?: string;
}

export interface CloverVoidSuccessfulResponseDetails {
    readonly outcome: typeof CloverVoidOutcome.VOIDED | typeof CloverVoidOutcome.REFUNDED;
    readonly httpStatus: 200;
    readonly body: CloverVoidResponseBody;
}

export interface CloverVoidRejectedResponseDetails {
    readonly outcome: typeof CloverVoidOutcome.REJECTED;
    readonly httpStatus: number;
    readonly body: CloverVoidErrorBody;
}

export interface CloverVoidMalformedResponseDetails {
    readonly outcome: typeof CloverVoidOutcome.MALFORMED_RESPONSE;
    readonly httpStatus: number;
    readonly body: unknown;
    readonly message: string;
}

export type CloverVoidResponseDetails =
    | CloverVoidSuccessfulResponseDetails
    | CloverVoidRejectedResponseDetails
    | CloverVoidMalformedResponseDetails;
